import type { CreateOfferInputPayload, UpdateOfferInputPayload } from '../../../../../hooks/Marketing';
import type { DefinitionFormData } from '../CampaignDefinitionStep';
import {
  ConditionPrefix,
  editConditionId,
  NO_DISCOUNT_ID,
  OfferFormData,
  USE_CONDITION_ID_BY_PREREQ,
  conditionKeys,
  NO_PREREQ_ID,
  triggerConditionsPayloadKey,
} from './offerForm';

type OfferBody = Omit<CreateOfferInputPayload, 'communicationInput'>;

type EditionContext = Pick<DefinitionFormData, 'channel' | 'isPreEdited' | 'editionDateStart' | 'editionDateEnd'>;

const pickConditions = (offer: OfferFormData, prefix: ConditionPrefix): Record<string, string> =>
  Object.fromEntries(conditionKeys(prefix).map(key => [key, offer[key]]));

export const buildOfferBody = (o: OfferFormData, { channel, isPreEdited, editionDateStart, editionDateEnd }: EditionContext): OfferBody => {
  const discountTypeId = o.discountTypeId !== null && o.discountTypeId !== NO_DISCOUNT_ID ? o.discountTypeId : undefined;
  // Only cashdesk coupons have an edition condition; a pre-edited offer has none either.
  const hasEditCondition = channel === 'cashdesk' && !isPreEdited;
  const editStartDate = hasEditCondition ? editionDateStart : '';
  const editEndDate = hasEditCondition ? editionDateEnd : '';
  const hasUseCondition = o.prereqId !== null && o.prereqId !== NO_PREREQ_ID;
  const conditionsKey = triggerConditionsPayloadKey(o.triggerId);
  return {
    title: o.title.trim() || undefined,
    description: o.description.trim() || undefined,
    offerNumber: o.offerNumber.trim() || undefined,
    startValidityDate: o.startValidityDate || undefined,
    endValidityDate: o.endValidityDate || undefined,
    nbMaxEdit: channel === 'cashdesk' && o.nbMaxEdit ? Number(o.nbMaxEdit) : undefined,
    nbMaxUse: o.nbMaxUse ? Number(o.nbMaxUse) : undefined,
    refDiscountTypeId: discountTypeId,
    payloadRefDiscountType: discountTypeId !== undefined
      ? { remiseMode: o.remiseMode, remiseValue: o.remiseValue, pointsMode: o.pointsMode, pointsValue: o.pointsValue }
      : undefined,
    refEditConditionId: hasEditCondition ? editConditionId(o) : undefined,
    payloadRefEditCondition: hasEditCondition && (o.triggerId || editStartDate || editEndDate)
      ? {
        ...pickConditions(o, 'trigger'),
        ...(conditionsKey && { [conditionsKey]: o.triggerConditions }),
        editStartDate,
        editEndDate,
      }
      : undefined,
    refUseConditionId: o.prereqId ? USE_CONDITION_ID_BY_PREREQ[o.prereqId] : undefined,
    payloadRefUseCondition: hasUseCondition || o.immediateUse || o.cumulable
      ? { ...(hasUseCondition && pickConditions(o, 'prereq')), immediateUse: o.immediateUse, cumulable: o.cumulable }
      : undefined,
  };
};

export const toMergePatch = (body: OfferBody): UpdateOfferInputPayload =>
  Object.fromEntries(Object.entries(body).map(([key, value]) => [key, value ?? null]));
